import { logError } from "./ConsoleLoggerService";

export enum LocalStorageKeys {
    // System (LifeAt managed) Stuff
    LastSeenSystemNotificationId = "LastSeenSystemNotificationId",
    /** @deprecated */
    AcceptedCookies = "AcceptedCookies",
    /** @deprecated */
    ViewedWelcomeTutorialV1 = "ViewedWelcomeTutorialV1",
    ViewedUpgradeModalJune2022 = "ViewedUpgradeModalJune2022",
    HasAuthenticatedOnDesktopAppToDesktopBuilderV1 = "HasAuthenticatedOnDesktopAppToDesktopBuilderV1",
    HasUserAcknowledgeTaskDndCta = "HasUserAcknowledgeTaskDndCta",
    HasUserAcknowledgedMenubarCta = "HasUserAcknowledgedMenubarCta",

    // User Experience Settings
    LightMode = "LightMode",
    AmbianceVolume = "AmbianceVolume",
    /** @deprecated */
    IsFirstVisitToLifeAtV2 = "IsFirstVisitToLifeAtV2",
    /** @deprecated */
    HasUserVisitedSinceFreeToDoList = "HasUserVisitedSinceFreeToDoList",
    /** @deprecated */
    WidgetKeysNotificationBadge = "WidgetKeysNotificationBadge",

    // User Metrics
    /** @deprecated */
    UserDailyStreakData = "UserDailyStreakData",

    // Spaces Settings
    /** @deprecated */
    SavedSpaces = "SavedSpaces",
    /** @deprecated */
    SavedSpacesV2 = "SavedSpacesV2",

    // Spaces Interactions
    ShuffleClicks = "ShuffleClicks",

    // WidgetSettings
    SavedWidgetLayoutInfo = "SavedWidgetLayoutInfo", // data type: SavedWidgetCanvasLayout

    // Pomodoro Timer Settings
    PomodoroAutoStart = "PomodoroAutoStart",
    PomodoroTimerVolume = "PomodoroTimerVolume",
    PomodoroDuration = "PomodoroDuration",
    ShortBreakDuration = "ShortBreakDuration",
    LongBreakDuration = "LongBreakDuration",

    // Music Widget Settings
    SavedMusicPlaylist = "SavedMusicPlaylist", // Note: the localstorage key for this is still "savedSpotifyPlaylist"

    // Breathing Widget
    HasSeenBreathingWidget = "HasSeenBreathingWidget",

    // Chat
    /** @deprecated */
    HasSeenChat = "HasSeenChat",
    /** @deprecated */
    HasSeenAiChat = "HasSeenAiChat",

    // Tests
    HasSeenWorkflowTest = "HasSeenWorkflowTest",

    // App access
    TrafficSource = "trafficSource", // this is used to block the app for users coming from ads
}

// Each key should be equivalent to the above Enum
// Value is what was stored in localstorage (carried over from LifeAt.io MVP site, ca. March-Sept 2021)
const LocalStorageItemKeyMap = {
    // System (LifeAt managed) Stuff
    LastSeenSystemNotificationId: "lastSeenSystemNotificationId",
    /** @deprecated */
    AcceptedCookies: "acceptedCookies",
    /** @deprecated */
    ViewedWelcomeTutorialV1: "viewedWelcomeTutorialV1",
    ViewedUpgradeModalJune2022: "viewedUpgradeModalJune2022",
    HasAuthenticatedOnDesktopAppToDesktopBuilderV1: "hasAuthenticatedOnDesktopAppToDesktopBuilderV1",
    HasUserAcknowledgeTaskDndCta: "hasUserAcknowledgeTaskDndCta",
    HasUserAcknowledgedMenubarCta: "hasUserAcknowledgedMenubarCta",

    // User Experience Settings
    LightMode: "lightMode",
    AmbianceVolume: "ambianceVolume",
    /** @deprecated */
    IsFirstVisitToLifeAtV2: "isFirstVisitToLifeAtV2",
    /** @deprecated */
    HasUserVisitedSinceFreeToDoList: "hasUserVisitedSinceFreeToDoList",
    /** @deprecated */
    WidgetKeysNotificationBadge: "widgetKeysNotificationBadge",

    // User Metrics
    /** @deprecated */
    UserDailyStreakData: "userDailyStreakData", // data formatted as UserStreakMetrics (UserMetrics.ts)

    // Spaces Settings
    SavedSpaces: "likedSpaces",
    SavedSpacesV2: "savedSpacesV2",

    // Spaces Interactions
    ShuffleClicks: "shuffleClicks",

    // Widget Settings
    SavedWidgetLayoutInfo: "savedWidgetLayoutInfo",

    // Pomodoro Timer Widget Settings
    PomodoroAutoStart: "autoStartTimer",
    PomodoroTimerVolume: "timerVolume",
    PomodoroDuration: "pomodoroDur",
    ShortBreakDuration: "shortBreakDur",
    LongBreakDuration: "longBreakDur",

    // Music Widget Settings
    SavedMusicPlaylist: "savedSpotifyPlaylist", // Note: the localstorage key for this is still "savedSpotifyPlaylist"

    // Breathing Widget
    HasSeenBreathingWidget: "HasSeenBreathingWidget",

    // Chat
    HasSeenChat: "HasSeenChat",
    HasSeenAiChat: "HasSeenAiChat",
};

/**
 * Save some data to local storage.
 * @param itemKey Key to save the data under.
 * @param itemVal Data to save.
 * @return true if the item was saved, false if not (itemKey was invalid).
 * @deprecated (soft deprecation) Use hook: useLocalStorage
 */
export const saveToLocalStorage = (itemKey: LocalStorageKeys, itemVal: any): boolean => {
    // Do not allow saving if the key is not added to the map.
    if (verifyItemKeyValid(itemKey)) {
        localStorage.setItem(LocalStorageItemKeyMap[itemKey], JSON.stringify(itemVal));
        return true;
    }
    return false;
};

/**
 * Retrieve data to local storage.
 * @param itemKey Key to fetch the data for.
 * @return Parsed data if found, otherwise undefined.
 * @deprecated (soft deprecation) Use hook: useLocalStorage
 */
export const fetchFromLocalStorage = (itemKey: LocalStorageKeys): any | undefined => {
    if (verifyItemKeyValid(itemKey)) {
        if (LocalStorageItemKeyMap[itemKey] != null) {
            const storageVal: string = localStorage.getItem(LocalStorageItemKeyMap[itemKey]);
            if (storageVal !== undefined && storageVal !== null) {
                return JSON.parse(storageVal);
            }
        }
    }
    return undefined;
};

/**
 * Check if the key is valid for a local storage item.
 * @param itemKey Key to be validated.
 * @deprecated TODO: move to useLocalStorage
 */
const verifyItemKeyValid = (itemKey: string): boolean => {
    let keyValid = false;
    if (itemKey in LocalStorageKeys && LocalStorageItemKeyMap.hasOwnProperty(itemKey)) {
        keyValid = true;
    } else {
        logError(`Local Storage Key not found: [${itemKey}] - Please 
                verify your key is an entry in LocalStorageItemKeyMap.`);
    }
    return keyValid;
};

/**
 * @deprecated Do not use.
 * @param itemKey
 */
export const removeItemFromLocalStorage = (itemKey: LocalStorageKeys): void => {
    localStorage.removeItem(itemKey);
};
